import { MouseEventHandler, Dispatch, SetStateAction } from 'react';
import {
  DevHubBackdrop,
  DevHubBox,
  DevHubButton,
  DevHubModal,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { gql, useMutation } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { Submitted } from './Submitted';
import { ResponsibleParty, RolesQueryVariables } from '../types/RoleTypes';

const DeleteMutation = gql`
  mutation DeleteResponsibility($input: DeleteResponsibilityInput!) {
    resources {
      responsibilities {
        deleteResponsibility(input: $input) {
          success
        }
      }
    }
  }
`;

type Props = {
  party: ResponsibleParty;
  displayName: string;
  handleOpen: MouseEventHandler<HTMLButtonElement>;
  handleClose: MouseEventHandler<HTMLButtonElement>;
  open: boolean;
  appId: number;
  responsibilityId: number;
  rolesQuery: DocumentNode;
  rolesVariables: RolesQueryVariables;
  submitted: boolean;
  setSubmitted: Dispatch<SetStateAction<boolean>>;
};

/**
 * Delete a party from a responsibility
 * @param props props of the component
 * @param props.party Party Object
 * @param props.displayName Name of the role
 * @param props.handleOpen Event Handler for opening the modal
 * @param props.handleClose Event Handler for closing the modal
 * @param props.open boolean state of the modal
 */
export function DeleteResponsibility(props: Props) {
  const [deleteResponsibility, { data, loading, error }] = useMutation(
    DeleteMutation,
    {
      refetchQueries: [
        { query: props.rolesQuery, variables: props.rolesVariables },
        'GetAppRolesAndAccessPolicy',
      ],
    },
  );

  const handleSubmit = () => {
    const deleteInput = {
      applicationId: props.appId,
      responsibilityId: props.responsibilityId,
    };
    deleteResponsibility({ variables: { input: deleteInput } });
    props.setSubmitted(true);
  };

  const getTitle = () => {
    if (props?.party?.__typename === 'Group') {
      return `Are you sure you want to delete ${props?.party?.name} from ${props?.displayName}?`;
    }
    return `Are you sure you want to delete ${props?.party?.firstName} ${props?.party?.lastName} from ${props?.displayName}?`;
  };

  return (
    <DevHubModal
      open={props.open}
      onClose={props.handleClose}
      closeAfterTransition
      BackdropComponent={DevHubBackdrop}
      BackdropProps={{ timeout: 500 }}
    >
      {props.submitted ? (
        <Submitted
          error={error}
          submitted={props.submitted}
          loading={loading}
          handleClose={props.handleClose}
          success={
            data?.resources?.responsibilities?.deleteResponsibility?.success
          }
          errorMessage="Error deleting responsibility."
        />
      ) : (
        <>
          <DevHubTypography paragraph>{getTitle()}</DevHubTypography>
          <DevHubBox
            display="flex"
            justifyContent="space-around"
            alignItems="center"
          >
            <DevHubButton
              variant="contained"
              color="primary"
              onClick={props.handleClose}
            >
              Cancel
            </DevHubButton>
            <DevHubButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Confirm
            </DevHubButton>
          </DevHubBox>
        </>
      )}
    </DevHubModal>
  );
}
